import { Logger, LoggerService } from '@Shared/logger';
import { DomainUtil } from '@Shared/utils';
import {
  type Context,
  createFeaturevisor,
  type DatafileContent,
  Featurevisor,
} from '@featurevisor/sdk';
import { APP_EVENTS } from '@Core';
import {
  Bus,
  OnAuthDeterminedResult,
  OnEntitlementDeterminedResult,
} from '@Modules/event_module';
import { FeaturesRepository } from '../repositories/features.repository';
import {
  EMPTY_DATAFILE,
  FeatureEnv,
  FEATURES_CONST,
  FeaturesOptions,
  FeatureTarget,
  MNGAuthFeatures,
} from '../_models';

export class FeaturesService {
  private Logger: Logger = LoggerService.createLogger('FeaturesService');
  private static instance: FeaturesService;

  private repository: FeaturesRepository;
  private featurevisor: Featurevisor;
  private options: FeaturesOptions;
  private unsubscribeAuth: (() => void) | undefined;
  private unsubscribeEntitlements: (() => void) | undefined;

  static get Instance() {
    if (this.instance === null || this.instance === undefined) {
      this.instance = new FeaturesService();
    }
    return this.instance;
  }

  get Featurevisor(): Featurevisor {
    return this.featurevisor;
  }

  get IsReady(): boolean {
    return this.featurevisor !== undefined;
  }

  constructor() {
    this.Logger.trace('**** CTOR ****');
    this.repository = FeaturesRepository.Instance;
  }

  /**
   * Creates the Featurevisor instance. A failed datafile load falls back to an empty datafile so
   * that every evaluation returns its default value and the rest of the plugin keeps working.
   */
  public async init(options: FeaturesOptions): Promise<void> {
    this.Logger.trace('setting up Features Service...', options);
    this.options = options;

    const datafile = await this.loadDatafile(
      options.cacheDuration ?? FEATURES_CONST.defaultCacheDuration,
    );

    const context: Context = {
      site: DomainUtil.getRootDomain(),
      deviceSessionId: this.deviceSessionId(),
      userId: undefined,
      roles: [],
      subscriptionLevel: undefined,
      serviceCode: undefined,
      isAuthenticated: undefined,
      isEntitled: undefined,
    };

    this.featurevisor = createFeaturevisor({
      datafile,
      context: context,
      logLevel: options.logLevel ?? FEATURES_CONST.defaultLogLevel,
    });

    this.listenForAuth();
    this.listenForEntitlements();

    Bus.emit(APP_EVENTS.onFeaturesReady, {
      visor: this.featurevisor,
      context: this.featurevisor.getContext(),
    });
  }

  public async close(): Promise<void> {
    this.unsubscribeAuth?.();
    this.unsubscribeAuth = undefined;
    this.unsubscribeEntitlements?.();
    this.unsubscribeEntitlements = undefined;
    await this.featurevisor?.close();
    this.featurevisor = undefined;
  }

  public datafileUrl(): string | null {
    const env = this.detectEnv();
    const target = this.detectTarget();
    return `https://features.mngdigisubs.com/f/${env}/featurevisor-${target}.json`;
  }

  public detectEnv(): FeatureEnv {
    if (this.options?.env) return this.options.env;

    /** brought this override over from WP */
    const override = localStorage.getItem('MNG_FEATURE_FLAG_ENV_OVERRIDE');
    if (['dev', 'preprod', 'prod'].includes(override))
      return override as FeatureEnv;

    const hostname = window.location.hostname;
    if (['vipdev.lndo.site'].includes(hostname)) return 'dev';

    switch (hostname.split('.').shift()) {
      case 'develop':
        return 'dev';
      case 'pstest':
      case 'preprod':
      case 'staging':
        return 'preprod';
      default:
        return 'prod';
    }
  }

  public detectTarget(): FeatureTarget {
    if (this.options?.target) return this.options.target;

    const hostname = window.location.hostname;
    if (['vipdev.lndo.site'].includes(hostname)) return 'wp';

    switch (hostname.split('.').shift()) {
      case 'staging':
      case 'develop':
      case 'preprod':
      case 'www':
        return 'wp';
      default:
        return 'eedition';
    }
  }

  private async loadDatafile(
    cacheDuration: number,
  ): Promise<DatafileContent | string> {
    if (this.options.datafile) {
      // used for testing
      return this.options.datafile;
    }

    if (!this.datafileUrl()) {
      this.Logger.warn(
        'Unable to generate datafileUrl(). Using empty datafile',
      );
      return EMPTY_DATAFILE;
    }

    try {
      return await this.repository.fetchDatafile(
        this.datafileUrl(),
        cacheDuration,
      );
    } catch (error) {
      this.Logger.error('Error loading datafile, using empty datafile', error);
      return EMPTY_DATAFILE;
    }
  }

  private deviceSessionId(): string {
    const existingDeviceSessionId = localStorage.getItem(
      'MNG_DEVICE_SESSION_ID',
    );
    const uuidRegex = /^[a-z0-9-]{36}$/;
    if (existingDeviceSessionId && uuidRegex.test(existingDeviceSessionId))
      return existingDeviceSessionId;

    const newDeviceSessionId = self.crypto.randomUUID();
    localStorage.setItem('MNG_DEVICE_SESSION_ID', newDeviceSessionId);
    return newDeviceSessionId;
  }

  private listenForAuth(): void {
    if (this.unsubscribeAuth) return;

    this.unsubscribeAuth = Bus.on(
      APP_EVENTS.onAuthDetermined,
      (result: OnAuthDeterminedResult) => {
        const context: Context = {
          isAuthenticated: result.isAuthenticated,
        };
        if (result.isAuthenticated && result.user) {
          context.userId = result.user.auth0Id;
        }
        this.Logger.trace('Updating context from onAuthDetermined', context);
        this.featurevisor?.setContext(context, false);

        Bus.emit(APP_EVENTS.onFeaturesContextChanged, {
          source: APP_EVENTS.onAuthDetermined,
          visor: this.featurevisor,
          context: this.featurevisor.getContext(),
        });
      },
    );
  }

  private listenForEntitlements(): void {
    if (this.unsubscribeEntitlements) return;

    this.unsubscribeEntitlements = Bus.on(
      APP_EVENTS.onEntitlementsDetermined,
      (result: OnEntitlementDeterminedResult) => {
        const context: Context = {
          isEntitled: result.mngSession?.isEntitled || false,
        };
        if (result.entitlement) {
          context.userId = result.entitlement.uuid;
          context.subscriptionLevel = result.entitlement.entitlement_level;
          context.serviceCode = result.entitlement.entitlement_serviceCode;
          context.roles = result.entitlement['mng/roles'] || [];
        }
        this.Logger.trace(
          'Updating context from onEntitlementsDetermined',
          context,
        );
        this.featurevisor?.setContext(context, false);

        Bus.emit(APP_EVENTS.onFeaturesContextChanged, {
          source: APP_EVENTS.onEntitlementsDetermined,
          visor: this.featurevisor,
          context: this.featurevisor.getContext(),
        });
      },
    );
  }
}

export const Features: MNGAuthFeatures = {
  visor: () => FeaturesService.Instance.Featurevisor,
  env: FeaturesService.Instance.detectEnv(),
  target: FeaturesService.Instance.detectTarget(),
  datafileUrl: FeaturesService.Instance.datafileUrl(),
};
